import * as React from 'react';
import styled from 'styled-components';
import { CreateBoxContext } from '../utils/create-context-box';
import { useBoxScroll } from '../utils/use-box-scroll';
import { useClientRect } from '../utils/use-client-rect';

type Ref = React.RefObject<HTMLDivElement>;

const Outer = styled.div<{ ref: Ref; height?: string; isImmersive?: boolean }>`
  ${({ isImmersive }) => (isImmersive ? 'margin: 0 -8px;' : null)}
  box-sizing: border-box;
  height: ${({ height = '300vh' }) => height};
  position: relative;
`;

const Inner = styled.div<{ ref: Ref; height?: string }>`
  box-sizing: inherit;
  height: ${({ height = '100vh' }) => height};
  overflow: hidden;
  position: sticky;
  top: 0;
`;

export interface ScrollBoxProps extends React.PropsWithChildren {
  outerHeight?: string;
  innerHeight?: string;
  isImmersive?: boolean;
  onScroll?: (position: number) => void;
}

export const ScrollBox: React.FC<ScrollBoxProps> = ({
  children,
  outerHeight,
  innerHeight,
  isImmersive,
  onScroll,
}) => {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const outerRef: Ref = React.useRef<HTMLDivElement>(undefined as any);
  const innerRef: Ref = React.useRef<HTMLDivElement>(undefined as any);
  const viewBox = useClientRect(innerRef);
  const position = useBoxScroll({ outerRef, innerRef });

  React.useEffect(() => {
    if (onScroll) {
      onScroll(position);
    }
  }, [position]);

  return (
    <CreateBoxContext.Provider value={viewBox}>
      <Outer ref={outerRef} height={outerHeight} isImmersive={isImmersive}>
        <Inner ref={innerRef} height={innerHeight}>
          {children}
        </Inner>
      </Outer>
    </CreateBoxContext.Provider>
  );
};
